import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import {
  FiUsers, FiBookOpen, FiBarChart2, FiPlus, FiX, FiCheck,
  FiAward, FiTrendingUp, FiGrid,
} from "react-icons/fi"
import { HiOutlineAcademicCap } from "react-icons/hi"
import { COURSE_STATUS } from "../../../utils/constants"
import {
  getAllInstructorDetails,
  getAllStudentDetails,
  createCategory,
} from "../../../services/operations/adminAPI"
import { getAllCourses as getAdminCourses } from "../../../services/operations/adminAPI"
import { fetchCourseCategories } from "../../../services/operations/courseDetailsAPI"

const StatCard = ({ label, value, icon: Icon, color, bg, loading }) => (
  <div className="rounded-xl border border-richblack-700 bg-richblack-800 p-4 sm:p-5">
    <div className={`mb-3 grid h-10 w-10 place-items-center rounded-lg ${bg}`}>
      <Icon size={20} className={color} />
    </div>
    <p className="text-2xl font-bold text-richblack-5">
      {loading
        ? <span className="inline-block h-6 w-12 animate-pulse rounded bg-richblack-700" />
        : value}
    </p>
    <p className="mt-0.5 text-xs text-richblack-400">{label}</p>
  </div>
)

export default function Admin() {
  const { user } = useSelector((state) => state.profile)
  const { token } = useSelector((state) => state.auth)

  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ students: 0, instructors: 0, courses: 0, published: 0 })
  const [recentCourses, setRecentCourses] = useState([])
  const [categories, setCategories] = useState([])

  const [showCatModal, setShowCatModal] = useState(false)
  const [catName, setCatName] = useState("")
  const [catDesc, setCatDesc] = useState("")
  const [catLoading, setCatLoading] = useState(false)

  const loadData = async () => {
    setLoading(true)
    const [students, instructors, courses, cats] = await Promise.all([
      getAllStudentDetails(),
      getAllInstructorDetails(),
      getAdminCourses(),
      fetchCourseCategories(),
    ])
    setStats({
      students: students?.length ?? 0,
      instructors: instructors?.length ?? 0,
      courses: courses?.length ?? 0,
      published: courses?.filter((c) => c.status === COURSE_STATUS.PUBLISHED)?.length ?? 0,
    })
    setRecentCourses(courses?.slice(0, 6) ?? [])
    setCategories(cats ?? [])
    setLoading(false)
  }

  useEffect(() => { loadData() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCategory = async () => {
    if (!catName.trim()) return
    setCatLoading(true)
    const ok = await createCategory({ name: catName, description: catDesc }, token)
    if (ok) {
      setShowCatModal(false)
      setCatName("")
      setCatDesc("")
      const cats = await fetchCourseCategories()
      setCategories(cats ?? [])
    }
    setCatLoading(false)
  }

  const STATS = [
    { label: "Total Students", value: stats.students, icon: FiUsers, color: "text-blue-400", bg: "bg-blue-400/10" },
    { label: "Instructors", value: stats.instructors, icon: HiOutlineAcademicCap, color: "text-yellow-400", bg: "bg-yellow-400/10" },
    { label: "Total Courses", value: stats.courses, icon: FiBookOpen, color: "text-caribbeangreen-300", bg: "bg-caribbeangreen-300/10" },
    { label: "Published", value: stats.published, icon: FiTrendingUp, color: "text-pink-300", bg: "bg-pink-300/10" },
  ]

  const QUICK_LINKS = [
    { label: "Students", path: "/dashboard/all-students", color: "border-blue-500/30 bg-blue-500/10 text-blue-400", icon: FiUsers },
    { label: "Instructors", path: "/dashboard/all-instructors", color: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400", icon: HiOutlineAcademicCap },
    { label: "All Courses", path: "/dashboard/all-courses", color: "border-caribbeangreen-300/30 bg-caribbeangreen-300/10 text-caribbeangreen-300", icon: FiBookOpen },
    { label: "Add Course", path: "/dashboard/add-course", color: "border-pink-400/30 bg-pink-400/10 text-pink-400", icon: FiPlus },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-richblack-5">Admin Dashboard</h1>
          <p className="mt-0.5 text-sm text-richblack-400">
            Welcome back, {user?.firstName}. Here's your platform overview.
          </p>
        </div>
        <div className="grid h-10 w-10 place-items-center rounded-xl border border-richblack-700 bg-richblack-800">
          <FiGrid size={18} className="text-yellow-50" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {STATS.map((s) => (
          <StatCard key={s.label} {...s} loading={loading} />
        ))}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {QUICK_LINKS.map(({ label, path, color, icon: Icon }) => (
          <Link
            key={path}
            to={path}
            className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-opacity hover:opacity-80 ${color}`}
          >
            <Icon size={15} />
            {label}
          </Link>
        ))}
      </div>

      {/* Categories + Recent Courses */}
      <div className="grid gap-6 lg:grid-cols-3">

        {/* Categories panel */}
        <div className="rounded-xl border border-richblack-700 bg-richblack-800 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FiAward size={15} className="text-yellow-50" />
              <h2 className="text-sm font-semibold text-richblack-5">Categories</h2>
            </div>
            <button
              onClick={() => setShowCatModal(true)}
              className="flex items-center gap-1 rounded-lg bg-yellow-50 px-3 py-1.5 text-xs font-bold text-richblack-900 transition-colors hover:bg-yellow-100"
            >
              <FiPlus size={12} /> New
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {loading && Array(4).fill(0).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-lg bg-richblack-700" />
            ))}
            {!loading && categories.length === 0 && (
              <p className="py-4 text-center text-xs text-richblack-500">No categories yet</p>
            )}
            {!loading && categories.map((cat) => (
              <div key={cat._id} className="flex items-center gap-2.5 rounded-lg bg-richblack-700/60 px-3 py-2.5">
                <FiAward size={12} className="flex-shrink-0 text-yellow-50" />
                <span className="truncate text-sm text-richblack-100">{cat.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Courses */}
        <div className="rounded-xl border border-richblack-700 bg-richblack-800 p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FiBarChart2 size={15} className="text-caribbeangreen-300" />
              <h2 className="text-sm font-semibold text-richblack-5">Recent Courses</h2>
            </div>
            <Link to="/dashboard/all-courses" className="text-xs text-yellow-50 hover:underline">
              View all
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {loading
              ? Array(6).fill(0).map((_, i) => (
                  <div key={i} className="h-28 animate-pulse rounded-lg bg-richblack-700" />
                ))
              : recentCourses.length === 0
              ? (
                <div className="col-span-3 py-10 text-center text-sm text-richblack-500">
                  No courses yet
                </div>
              )
              : recentCourses.map((c) => (
                  <div key={c._id} className="overflow-hidden rounded-lg border border-richblack-700 bg-richblack-700/40">
                    {c.thumbnail && (
                      <img
                        src={c.thumbnail}
                        alt={c.courseName}
                        className="h-20 w-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none" }}
                      />
                    )}
                    <div className="p-2.5">
                      <p className="truncate text-xs font-medium text-richblack-5">{c.courseName}</p>
                      <div className="mt-1 flex items-center justify-between">
                        <p className="text-[10px] text-richblack-400">₹{c.price}</p>
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                          c.status === COURSE_STATUS.PUBLISHED
                            ? "bg-caribbeangreen-300/15 text-caribbeangreen-300"
                            : "bg-richblack-600 text-richblack-400"
                        }`}>
                          {c.status === COURSE_STATUS.PUBLISHED ? "Live" : "Draft"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </div>

      {/* Create Category Modal */}
      {showCatModal && (
        <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-richblack-900/80 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="w-full overflow-hidden rounded-t-2xl border border-richblack-600 bg-richblack-800 shadow-2xl sm:max-w-md sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-richblack-700 px-5 py-4">
              <h3 className="text-sm font-semibold text-richblack-5">Create Category</h3>
              <button
                onClick={() => { setShowCatModal(false); setCatName(""); setCatDesc("") }}
                className="rounded-lg p-1.5 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-richblack-5"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-richblack-200">
                  Name <sup className="text-pink-200">*</sup>
                </label>
                <input
                  value={catName}
                  onChange={(e) => setCatName(e.target.value)}
                  placeholder="e.g. Web Development"
                  className="form-style w-full"
                  onKeyDown={(e) => e.key === "Enter" && !catLoading && handleCreateCategory()}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-richblack-200">Description</label>
                <textarea
                  rows={3}
                  value={catDesc}
                  onChange={(e) => setCatDesc(e.target.value)}
                  placeholder="Brief description of this category"
                  className="form-style w-full resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 border-t border-richblack-700 px-5 py-4">
              <button
                type="button"
                disabled={catLoading}
                onClick={() => { setShowCatModal(false); setCatName(""); setCatDesc("") }}
                className="flex-1 rounded-xl border border-richblack-600 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:text-richblack-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={catLoading || !catName.trim()}
                onClick={handleCreateCategory}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-yellow-50 py-2.5 text-sm font-bold text-richblack-900 transition-colors hover:bg-yellow-100 disabled:opacity-60"
              >
                {catLoading ? "Creating…" : <><FiCheck size={14} /> Create</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
